import { cloneElement, isValidElement, type HTMLAttributes, type ReactNode } from 'react';
import type { ColumnsType, ColumnType } from 'antd/es/table';

/**
 * Helpers for Playwright test-automation attributes (data-testid / data-*).
 * Conventions: test IDs are kebab-case; enum-like data-* values are lowercase kebab-case.
 */

/** 'Ready for Approval' → 'ready-for-approval', 'Change (MoM)' → 'change-mom' */
export const toTestAttrValue = (value: string | number | null | undefined): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/** Lets data-* keys pass through antd's onRow / onCell / button-props types. */
export type TestAttrs = HTMLAttributes<HTMLElement> & Record<`data-${string}`, string>;

/**
 * antd pagination itemRender: tags items as pagination-page (with data-page), pagination-prev,
 * pagination-next, pagination-jump-prev and pagination-jump-next.
 */
export const testPaginationItemRender = (page: number, type: string, element: ReactNode): ReactNode =>
  isValidElement(element)
    ? cloneElement(element, { 'data-testid': `pagination-${type}`, 'data-page': String(page) } as TestAttrs)
    : element;

/**
 * Adds data-testid + data-column to every body cell (testId) and header cell (column-header),
 * keeping any existing onCell / onHeaderCell output. antd sets aria-sort on sorted headers.
 * data-column is the kebab-cased header label; columns with a render-function title
 * need an entry in `labels` (keyed by column key).
 */
export const withTestColumnAttrs = <T,>(
  columns: ColumnsType<T>,
  testId: string,
  labels: Record<string, string> = {},
): ColumnsType<T> =>
  columns.map((col) => {
    const key = String(col.key ?? '');
    const columnName = toTestAttrValue(labels[key] ?? (typeof col.title === 'string' ? col.title : key));
    return {
      ...col,
      onCell: (record: T, index?: number) =>
        ({ ...col.onCell?.(record, index), 'data-testid': testId, 'data-column': columnName }) as TestAttrs,
      onHeaderCell: (column: Parameters<NonNullable<ColumnType<T>['onHeaderCell']>>[0]) =>
        ({ ...col.onHeaderCell?.(column), 'data-testid': 'column-header', 'data-column': columnName }) as TestAttrs,
    };
  });
